import React, { useState, useEffect } from "react";
import NotesModal from "../NotesModal/NotesModal";
import DepassementModal from "../DepassementModal/DepassementModal";
import DelaiResolutionParametrageModal from "../DelaiResolutionParametrageModal/DelaiResolutionParametrageModal";
import { ticketService } from "../../../../service/ticketService";
import {
  getParcoursColor,
  getPriseEnChargeColor,
  getResolutionColor,
  getResolutionParamColor,
} from "../../../../utils/savUtils";

import styles from "./RatiosCards.module.css";

/* ---------- Icons ---------- */

const StarIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#FFC107">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const AlertIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#EF4444"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M4.5 3L7.5 6L4.5 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ---------- Main Component ---------- */

const RatiosCards = ({ ratios, notes }) => {
  const averageRating = notes?.moyenne ? notes.moyenne.toFixed(1) : "0.0";
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDelaiResolutionParametrageModal, setShowDelaiResolutionParametrageModal] = useState(false);
  const [showDepassementModal, setShowDepassementModal] = useState(false);
  const [depassementEtablissements, setDepassementEtablissements] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const month = (now.getMonth() + 1).toString().padStart(2, "0");
        const firstDay = `${year}-${month}-01`;
        const lastDay = `${year}-${month}-${new Date(
          year,
          now.getMonth() + 1,
          0
        ).getDate()}`;

        const response = await ticketService.getEstablishmentsWithMoreThanFourTickets({
          dateDebut: firstDay,
          dateFin: lastDay,
        });

        setDepassementEtablissements(
          response?.data?.result?.etablissementsAvecDepassementNbreTicketsMois ?? []
        );
      } catch (error) {
        console.error("Error fetching establishments:", error);
      }
    };

    fetchData();
  }, []);

  const handleCardClick = (cardKey) => {
    switch (cardKey) {
      case "averageRating":
        setShowNotesModal(true);
        break;
      case "etablissementsAvecDepassementNbreTicketsMois":
        setShowDepassementModal(true);
        break;
      case "delaiResolutionParametrageMoyen":
        setShowDelaiResolutionParametrageModal(true);
        break;
      default:
        break;
    }
  };

  const cardData = [
    {
      key: "delaiPriseEnChargeMoyen",
      title: "Prise en charge",
      value: ratios?.delaiPriseEnChargeMoyen || "--",
      unit: "",
      colorClass: getPriseEnChargeColor(ratios?.delaiPriseEnChargeMoyen),
      hasDetails: false,
    },
    {
      key: "delaiParcouruMoyen",
      title: "Moyen de parcours",
      value: ratios?.delaiParcouruMoyen || "--",
      unit: "",
      colorClass: getParcoursColor(ratios?.delaiParcouruMoyen),
      hasDetails: false,
    },
    {
      key: "delaiResolutionMoyen",
      title: "Moyen de résolution",
      value: ratios?.delaiResolutionMoyen || "--",
      unit: "",
      colorClass: getResolutionColor(ratios?.delaiResolutionMoyen),
      hasDetails: false,
    },
    {
      key: "delaiResolutionParametrageMoyen",
      title: "Résolution (paramétrage)",
      value: ratios?.delaiResolutionParametrageMoyen || "--",
      unit: "",
      colorClass: getResolutionParamColor(ratios?.delaiResolutionParametrageMoyen),
      hasDetails: true,
    },
    ...(averageRating > 0
      ? [
          {
            key: "averageRating",
            title: "Note moyenne",
            value: averageRating,
            unit: "",
            colorClass: "yellow",
            hasDetails: true,
            icon: <StarIcon size={14} />,
          },
        ]
      : []),
    ...(depassementEtablissements?.length > 0
      ? [
          {
            key: "etablissementsAvecDepassementNbreTicketsMois",
            title: "Étab. en dépassement",
            value: depassementEtablissements?.length || 0,
            unit: "",
            colorClass: "red",
            hasDetails: true,
            icon: <AlertIcon size={14} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <div className={styles.container}>
        {cardData.map((card) => {
          const colorCls = styles[card.colorClass] || "";
          return (
            <div
              key={card.key}
              className={`${styles.card} ${colorCls} ${
                card.hasDetails ? styles.clickable : ""
              }`}
              onClick={
                card.hasDetails ? () => handleCardClick(card.key) : undefined
              }
              title={card.title}
            >
              <div className={styles.content}>
                <div className={styles.titleRow}>
                  <p className={styles.title}>{card.title}</p>
                  {card.hasDetails && (
                    <span className={styles.ctaArrow}>
                      <ChevronIcon />
                    </span>
                  )}
                </div>
                <div className={styles.valueRow}>
                  <span className={styles.value}>{card.value}</span>
                  {card.unit && <span className={styles.unit}>{card.unit}</span>}
                  {card.icon && <span className={styles.icon}>{card.icon}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <NotesModal
        notes={notes}
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
      />
      <DelaiResolutionParametrageModal
        stat={ratios.stat}
        isOpen={showDelaiResolutionParametrageModal}
        onClose={() => setShowDelaiResolutionParametrageModal(false)}
      />
      <DepassementModal
        isOpen={showDepassementModal}
        onClose={() => setShowDepassementModal(false)}
      />
    </>
  );
};

export default RatiosCards;